import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  useTheme,
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Grid,
  Container,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import FormTextField from "../components/form/FormTextField";
import Backdrop from "../components/common/Backdrop";
import { useLoginMutation } from "../features/api/authApiSlice";
import { setCredentials } from "../features/slices/authSlice";
import { selectCurrentToken } from "../features/slices/authSlice";
import { setAlert } from "../features/slices/alertSlice";
import { yupResolver } from "@hookform/resolvers/yup";
import Image from "../components/common/Image";
import { useTranslation } from "react-i18next";
import ImageCarousel from "../components/common/ImageCarousel";
import * as Constant from "../features/constant";
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {
  useAddFingerprintMutation,
} from "../features/api/publicApiSlice";

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const token = useSelector(selectCurrentToken);
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const lang = localStorage.getItem("language");

  // const bonusSliderImage_en = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  // const bonusSliderImage_bd = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  const BONUS_SLIDER_BASE = process.env.REACT_APP_BONUS_SLIDER || "";

  function getBonusSliderImages(envVar) {
    if (!envVar) return [];
    return envVar.split(",").map(function (fileName) {
      return BONUS_SLIDER_BASE + fileName.trim();
    });
  }

  const bonusSliderImage_en = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_EN);
  const bonusSliderImage_bd = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_BD);

  const loginSchema = yup.object().shape({
    username: yup.string().required(t("validation.username")),
    password: yup.string().required(t("validation.password")),
  });

  // utilize the other hooks to display loader, display error message or anything
  const [login, { data, isLoading, isError, isSuccess, error, reset }] =
    useLoginMutation();

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();

    return result;
  };

  const [addFingerprint, { fingerprintData }] =
    useAddFingerprintMutation();


  useEffect(() => {
    if (token) {
      navigate(Constant.getPath("/"));
    }
  }, []);

  const submitForm = async (data) => {
    let formData = new FormData();
    formData.append("username", data.username);
    formData.append("password", data.password);
    formData.append("grant_type", "password");

    const userData = await login(formData).unwrap();
    dispatch(setCredentials({ ...userData }));

    const fingerprint = await getFingerprint();

    await addFingerprint({
      fingerprint: fingerprint.visitorId,
      userAgent: navigator.userAgent,
    });
  };

  if (!isLoading && isSuccess) {

    window.loginListener = window.loginListener || window;

    dispatch(
      setAlert({
        isOpen: true,
        message: t("header.loginsuccessmessage"),
        variant: "success",
      })
    );
    navigate(Constant.getPath("/"));
    reset();

    const loginCompleteMessage = { event: 'af_login' };
    window.loginListener.postMessage(JSON.stringify(loginCompleteMessage));
  }

  if (!isLoading && isError) {
    dispatch(
      setAlert({
        isOpen: true,
        message: error.data.error_description,
        variant: "error",
      })
    );
    reset();
  }

  return (
    <Box backgroundColor={theme.palette.background.secondaryPaper} height="100vh">
      {/* <Box p={2} backgroundColor="black">
        <Grid container justifyContent="space-between">
          <Image
            width="178px"
            src={process.env.REACT_APP_LOGO}
            height="50px"
            onClick={null}
          />
          <IconButton
            onClick={() => {
              navigate("/");
            }}
          >
            <Close />
          </IconButton>
        </Grid>
      </Box> */}
      <Container>
        <Box mt={12}>
          <Box
            display="flex"
            alignItems="center"
            sx={{
              backgroundColor: theme.palette.primary.main,
            }}
            borderRadius={3}
          >
            <Box
              p={2}
              flexGrow={1}
              sx={{ cursor: "pointer", position: "relative" }}
              onClick={() => navigate(Constant.getPath("/login"))}
            >
              <Typography
                color={theme.palette.mobileLoginRegTitle}
                fontSize={14}
                fontWeight={800}
                align="center"
              >
                {t("mobileloginregister.login")}
              </Typography>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 10,
                  left: "50%",
                  transform: "translateX(-50%)",
                  borderBottom: "2px solid",
                  width: "20%",
                  borderColor: theme.palette.mobileLoginRegTitle,
                }}
              />
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
              p={2}
              flexGrow={1}
              sx={{ cursor: "pointer", position: "relative" }}
              onClick={() => navigate(Constant.getPath("/register"))}
            >
              <Typography
                fontSize={14}
                fontWeight={800}
                align="center"
                color="white"
              >
                {t("mobileloginregister.signup")}
              </Typography>
            </Box>
          </Box>
          <Box>
            <ImageCarousel
              banners={lang === 'en' ? bonusSliderImage_en : bonusSliderImage_bd}
              navigation={false}
              spaceBetween={0}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            sx={{ backgroundColor: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper }}
            justifyContent="center"
            pb={3}
            px={5}
          >
            <form onSubmit={handleSubmit(submitForm)}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PersonIcon sx={{ color: process.env.REACT_APP_COLOR_TEXT || undefined, mt: 2, mr: 1 }} />
                <Box flex={1}>
                  <FormTextField
                    label={t("mobileloginregister.username")}
                    control={control}
                    controllerName="username"
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <KeyIcon sx={{ color: process.env.REACT_APP_COLOR_TEXT || undefined, mt: 2, mr: 1 }} />
                <Box flex={1}>
                  <FormTextField
                    label={t("mobileloginregister.password")}
                    control={control}
                    controllerName="password"
                    type="password"
                    showPasswordToggle
                    size="small"
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="center"
                mt={isError ? "10px" : "30px"}
              >
                <Button variant="contained" type="submit">
                  {t("mobileloginregister.login")}
                </Button>
              </Box>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <a
                  style={{ color: theme.palette.primary.main }}
                  href="/forgotPassword"
                >
                  <Typography mt={2} fontSize={14}>
                    {t("mobileloginregister.forgotpassword")}
                  </Typography>
                </a>
              </div>

              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>

                <Typography mt={2} fontSize={14} style={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}>
                  {t("mobileloginregister.contactus")}
                  <a
                    style={{ color: theme.palette.primary.main }}
                    href="/ContactUs"
                  >
                    {t("mobileloginregister.customerservice")}
                  </a>
                  {"."}
                </Typography>
              </div>

            </form>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
